import { useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import useApiService from 'services/api'
import { INote } from 'models/contact_companies'
import { MOBILE_FOOTER_CLEARANCE } from 'styles/mobileTokens'
import { cn } from 'components/UI/cn'
import { FbLink } from 'components/FbUI/FbLink'
import { NoteManager } from 'components/Notes/NoteManager'
import { toast } from 'react-toastify'
import { useMountEffect } from 'hooks/useMountEffect'
import { useKeyboardInset } from 'hooks/useKeyboardInset'

type LocationState = {
  note: INote
  companyName?: string | null
  doorName?: string | null
  contactCompanyId?: string | null
  doorProxyId?: string | null
}

type FormValues = {
  note: string
  attachments: File[]
}

// Bottom clearance for the action row: the larger of the fixed mobile footer nav
// (keyboard closed) or the on-screen keyboard plus iOS's floating AutoFill/
// dismiss controls (keyboard open) — so Save/Cancel never fall below the fold,
// behind the keyboard, or under those OS controls (FIR-7597). The `--keyboard-*`
// vars are driven by useKeyboardInset() and are 0px while the keyboard is closed.
// Kept as an inline style so the shared MOBILE_FOOTER_CLEARANCE constant stays
// readable rather than an opaque arbitrary Tailwind class.
const ACTIONS_PADDING_BOTTOM = `max(${MOBILE_FOOTER_CLEARANCE}, calc(var(--keyboard-inset-height, 0px) + var(--keyboard-accessory-clearance, 0px)))`

export function MobileEditNotePage() {
  const navigate = useNavigate()
  const { state } = useLocation() as { state: LocationState | null }
  const originalNote = state?.note
  const [searchParams] = useSearchParams()
  const api = useApiService()
  const queryClient = useQueryClient()

  const contactCompanyIdParam = searchParams.get('contact_company_id')
  const doorProxyIdParam = searchParams.get('door_proxy_id')
  const companyNameParam = searchParams.get('company_name')
  const doorNameParam = searchParams.get('door_name')

  const companyDisplay =
    companyNameParam || (originalNote as any)?.company_name || ''
  const rawDoorDisplay = doorNameParam || (originalNote as any)?.door_name || ''

  const contactCompanyId =
    contactCompanyIdParam || (originalNote as any)?.contact_company_id
  const doorProxyId = doorProxyIdParam || (originalNote as any)?.door_proxy_id

  const hasDoor = !!rawDoorDisplay && !!doorProxyId
  const doorDisplay = hasDoor ? rawDoorDisplay : ''

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      note: originalNote?.content || '',
      attachments: [],
    },
  })

  const [attachmentsToDelete, setAttachmentsToDelete] = useState<number[]>([])

  // Keep Save/Cancel above the on-screen keyboard (FIR-7597).
  useKeyboardInset()

  useMountEffect(() => {
    return () => {
      setAttachmentsToDelete([])
      reset()
    }
  })

  const mutation = useMutation<void, Error, FormValues>({
    mutationFn: async (values) => {
      if (!originalNote) return
      if (attachmentsToDelete.length > 0) {
        await Promise.all(
          attachmentsToDelete.map((attachmentId) =>
            api.deleteNoteAttachment(originalNote.id, attachmentId)
          )
        )
      }

      await api.editNote(originalNote.id, values.note)

      if (values.attachments.length > 0) {
        await api.addAttachmentsToNote(originalNote.id, values.attachments)
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notes-infinite'] })
      await queryClient.refetchQueries({ queryKey: ['notes-infinite'] })
      toast.success('Note updated successfully')
      navigate(-1)
    },
  })

  if (!originalNote) {
    navigate(-1)
    return null
  }

  const isSubmitting = mutation.isPending

  const onSubmit = handleSubmit(async (values) => {
    await mutation.mutateAsync(values)
  })

  const handleCancel = () => {
    navigate(-1)
  }

  const handleDelete = () => {
    navigate(`/notes/${originalNote.id}/delete`, {
      state: { note: originalNote },
    })
  }

  return (
    // Fills the mobile layout's content area so the page never grows past the
    // viewport — the editor scrolls internally and the actions stay pinned.
    <div className="flex h-full flex-col bg-surface-sunken">
      <div className="shrink-0 bg-surface-sunken px-4 py-3">
        {/* Title on the left, the destructive Delete action pinned to the right. */}
        <div className="mb-2 flex items-center justify-between gap-3">
          <h1 className="m-0 text-xl font-semibold text-fg">Edit Note</h1>
          {/* Kept out of the button row (and away from the keyboard) so a
              destructive action never sits where Save/Cancel do. Not in the
              Figma, but retained as a deliberate feature — it's the only mobile
              path to delete a note. */}
          <button
            type="button"
            onClick={handleDelete}
            disabled={isSubmitting}
            className="shrink-0 cursor-pointer border-0 bg-transparent px-0 py-1 text-sm font-medium text-error-strong disabled:cursor-not-allowed disabled:opacity-50"
          >
            Delete
          </button>
        </div>

        {(companyDisplay || doorDisplay) && (
          <div className="flex items-center gap-2 text-sm">
            {companyDisplay && contactCompanyId && (
              <FbLink to={`/company/${contactCompanyId}`}>
                {companyDisplay}
              </FbLink>
            )}

            {companyDisplay && hasDoor && <span>|</span>}

            {hasDoor ? (
              <FbLink to={`/door/${doorProxyId}`}>{doorDisplay}</FbLink>
            ) : (
              <span className="italic text-fg-muted">{doorDisplay}</span>
            )}
          </div>
        )}
      </div>

      <div
        className={cn(
          'min-h-0 flex-1 overflow-y-auto bg-surface px-4',
          // Editor body height.
          '[&_.mobile-note-editor]:h-auto [&_.mobile-note-editor]:min-h-[200px] [&_.mobile-note-editor]:p-3',
          // Toolbar pinned to the bottom of the scroll area as content scrolls.
          '[&_.ql-toolbar]:sticky [&_.ql-toolbar]:bottom-0 [&_.ql-toolbar]:border-x-0 [&_.ql-toolbar]:border-b-0 [&_.ql-toolbar]:border-t [&_.ql-toolbar]:border-solid [&_.ql-toolbar]:!border-line [&_.ql-toolbar]:bg-surface [&_.ql-toolbar]:px-0 [&_.ql-toolbar]:py-2',
          '[&_.ql-container]:border-0 [&_.ql-container]:text-base',
          '[&_.ql-editor]:min-h-[150px] [&_.ql-editor]:p-0',
          '[&_.ql-editor.ql-blank::before]:text-fg-secondary [&_.ql-editor.ql-blank::before]:not-italic'
        )}
      >
        <NoteManager<FormValues>
          control={control}
          noteName="note"
          attachmentsName="attachments"
          label=""
          placeholder="Start writing note here"
          existingNote={originalNote}
          viewMode={false}
          editorClassName="mobile-note-editor"
          enableMentions
          onAttachmentsToDelete={setAttachmentsToDelete}
        />
      </div>

      <div
        className="flex shrink-0 gap-3 bg-surface-sunken px-4 pt-4"
        style={{ paddingBottom: ACTIONS_PADDING_BOTTOM }}
      >
        <button
          type="button"
          onClick={handleCancel}
          disabled={isSubmitting}
          className="flex-1 cursor-pointer rounded-lg !border border-solid !border-line bg-surface px-6 py-3.5 text-base font-medium text-fg disabled:cursor-not-allowed disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          className="flex-[1.5] cursor-pointer rounded-lg border-0 bg-brand-hover px-6 py-3.5 text-base font-semibold text-white enabled:hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50"
        >
          Save
        </button>
      </div>
    </div>
  )
}
